import { ReactNode } from "react"
import styled from "styled-components"
import { ArrowLeftIcon } from "../../icons/ArrowLeft"
import { ArrowRightIcon } from "../../icons/ArrowRight"
import { Typography } from "../../typography"
import { DeprecatedButton } from "../button"

const Container = styled.ul`
  display: flex;
  width: 100%;
  align-items: center;
  justify-content: center;
  & > li {
    padding: 0 0.2rem;
  }
`

const Item = styled.li<{ disabled: boolean; $itemUnselectable: boolean }>`
  display: flex;
  align-items: stretch;
  justify-content: stretch;

  cursor: ${({ disabled }) => (disabled ? "default" : "pointer")};

  & > * {
    color: ${({ theme, disabled }) => (disabled ? theme.colors.grey.$300 : theme.colors.grey.$600)};
    background: transparent;
    cursor: inherit;
    transition: all 0.3s ease;
  }

  & > *:active {
    ${({ theme, disabled }) => (disabled ? "" : `background: ${theme.colors.black.$600};`)}
  }

  ${({ $itemUnselectable }) =>
    $itemUnselectable
      ? `
    & > *:disabled {
      background: transparent;
    }
  `
      : ""}
`
const from = (start: number) => ({
  to: (end: number) => Array.from({ length: end - start + 1 }, (_, index) => index + start),
})
const MAX_TO_SHOW = 5
const TOTAL_SIBLINGS_COUNT = 4
const ONE_SIDE_SIBBLINGS_COUNT = TOTAL_SIBLINGS_COUNT / 2
const FIRST_PAGE_INDEX = 1
const DOTS = "..."

const getPages = (numberOfPages: number, currentPage: number): (number | "...")[] => {
  const LAST_PAGE_INDEX = numberOfPages

  if (MAX_TO_SHOW >= LAST_PAGE_INDEX) {
    return from(FIRST_PAGE_INDEX).to(LAST_PAGE_INDEX)
  }

  const firstLeftSibblingIndex = Math.max(currentPage - ONE_SIDE_SIBBLINGS_COUNT, 1)
  const lastRightSibblingCount = Math.min(currentPage + ONE_SIDE_SIBBLINGS_COUNT, LAST_PAGE_INDEX)
  const showLeftDot = firstLeftSibblingIndex >= TOTAL_SIBLINGS_COUNT
  const showRightDot = lastRightSibblingCount <= LAST_PAGE_INDEX - TOTAL_SIBLINGS_COUNT

  if (!showLeftDot && showRightDot) {
    return [...from(FIRST_PAGE_INDEX).to(MAX_TO_SHOW), DOTS, LAST_PAGE_INDEX]
  }

  if (showLeftDot && !showRightDot) {
    return [FIRST_PAGE_INDEX, DOTS, ...from(LAST_PAGE_INDEX - MAX_TO_SHOW).to(LAST_PAGE_INDEX)]
  }

  if (showLeftDot && showRightDot) {
    return [FIRST_PAGE_INDEX, DOTS, ...from(firstLeftSibblingIndex).to(lastRightSibblingCount), DOTS, LAST_PAGE_INDEX]
  }

  return from(FIRST_PAGE_INDEX).to(LAST_PAGE_INDEX)
}

export type DeprecatedItemProps = {
  pageNumber: number
  children: ReactNode
  selected: boolean
  onPageChange?: (pageClicked: number) => void
}
export type PaginationProps = {
  totalPages: number
  currentPage: number
  onPageChange?: (pageClicked: number) => void
  renderItem?: (props: DeprecatedItemProps) => ReactNode
}

const defaultRenderItem = ({ pageNumber, children, selected, onPageChange }: DeprecatedItemProps) => {
  return (
    <DeprecatedButton
      isLoading={false}
      $variant="round"
      size="small"
      onClick={() => onPageChange && onPageChange(pageNumber)}
      disabled={selected}
    >
      {children}
    </DeprecatedButton>
  )
}

export const DeprecatedPagination = ({
  totalPages,
  currentPage,
  onPageChange,
  renderItem = defaultRenderItem,
  ...navProps
}: PaginationProps) => {
  const prevPageDisabled = currentPage === 1
  const prevPage = renderItem({
    pageNumber: Math.max(currentPage - 1, 1),
    children: <ArrowLeftIcon />,
    selected: prevPageDisabled,
    onPageChange,
  })
  const nextPageDisabled = currentPage >= totalPages
  const nextPage = renderItem({
    pageNumber: Math.min(currentPage + 1, totalPages),
    children: <ArrowRightIcon />,
    selected: nextPageDisabled,
    onPageChange,
  })

  if (!Number.isInteger(totalPages)) {
    console.error("Pagination: totalPages must be an integer, the pagination component will not be rendered.")
    return null
  }

  if (!Number.isInteger(currentPage)) {
    console.error("Pagination: currentPage must be an integer, the pagination component will not be rendered.")
    return null
  }

  return (
    <Typography as="nav" $variant="body-4-medium" role="navigation" aria-label="Pagination" {...navProps}>
      <Container>
        <Item disabled={prevPageDisabled} $itemUnselectable>
          {prevPage}
        </Item>

        {getPages(totalPages, currentPage).map((pageNumber, i) => {
          if (pageNumber === DOTS) {
            return (
              <li key={pageNumber + i}>
                <span>{DOTS}</span>
              </li>
            )
          }

          const content = renderItem({
            pageNumber,
            children: pageNumber,
            selected: pageNumber === currentPage,
            onPageChange,
          })

          return (
            <Item disabled={pageNumber === currentPage} $itemUnselectable={false} key={pageNumber}>
              {content}
            </Item>
          )
        })}
        <Item disabled={nextPageDisabled} $itemUnselectable>
          {nextPage}
        </Item>
      </Container>
    </Typography>
  )
}
